import { default as React, FC, Fragment, useEffect, useState } from 'react';

import { LeftOutlined } from '@ant-design/icons';
import { Button, Modal, notification } from 'antd';
import routePaths from 'config/routes';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import apiPaths from 'services/apiPaths';
import { setFooterHeight } from 'src/actions/appActions';
import { refreshUserInfo } from 'src/actions/authActions';
import { UpdateUserPoints } from 'src/actions/authActions';
import CheckoutLotteryDetails from 'src/components/experienceCheckout/CheckoutLotteryDetails';
import BasicHeader from 'src/components/layout/BasicHeader';
import Link from 'src/components/Link';
import { IRootReducers } from 'src/reducers';
import { ResourceTypeEnum } from 'src/shared/enums';
import { IChallenge, IChallengeCheckoutLottery } from 'src/shared/models';
import {
  checkUserResourcePoints,
  completeChallengeUserResource,
  customFormatMessage,
  dispatchPageViewEvent,
  isError,
  scrollDown,
} from 'utils';
import CheckoutEndPage from 'src/pages/experience/checkout-end';
import { useRouter } from 'next/router';

interface OwnProps {
  config: IChallengeCheckoutLottery;
  updateChallenge: React.Dispatch<React.SetStateAction<IChallenge>>;
  updateUserPoints: (
    userPoints: number
  ) => (dispatch: Dispatch<UpdateUserPoints>) => void;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    userPoints: state.auth.userPoints,
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ setFooterHeight, refreshUserInfo }, dispatch);

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const CheckoutLottery: FC<Props> = ({
  challenge,
  config,
  updateChallenge,
  updateUserPoints,
  userPoints,
  setFooterHeight,
  refreshUserInfo,
  user,
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const { buttonName, isCompleted } = config;
  const textTopButton: string = config.textTopButton;
  const orderTitle: string = config.orderTitle;
  const orderDescription: string = config.orderDescription;

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [remainingChallengePoints, setRemainingChallengePoints] = useState<
    number | null
  >(null);

  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    remainingChallengePoints,
    challenge?.challengeType
  );
  const showCheckoutButton = !showModal && config.isChallengeInDate;
  const pointsLabel = Math.abs(remainingChallengePoints);

  useEffect(() => {
    if (showCheckoutButton) {
      setFooterHeight('checkout');
    }
    return () => {
      setFooterHeight(null);
    };
  }, [showModal, config.isChallengeInDate]);

  useEffect(() => {
    // refreshUserInfo();
    if (challenge?.challengeResources) {
      const { challengeResources } = challenge;
      const experiencePoints = challengeResources
        .filter(
          (resource) =>
            resource.config?.points && resource.config.isCompleted === false
        )
        .reduce((prev, curr) => prev + curr.config.points, 0);
      setRemainingChallengePoints(experiencePoints);
    }
  }, [challenge, userPoints]);

  const performLotteryCheckout = async () => {
    try {
      setLoading(true);
      const result = await completeChallengeUserResource({
        dataPath: apiPaths.CHALLENGES.COMPLETE_EXPERIENCE,
        idResource: config.idResource,
        updateChallenge,
        updateUserPoints,
      });

      if (isError(result)) {
        notification.error({
          message: formatMessage({ id: 'app.error.generic' }),
        });
      } else {
        setShowModal(false);
        setShowSuccessModal(true);
        // Send dataLayer with "/checkout-end" and the end of virtualUrl attribute
        // The rest of the data will be set by the dispatchPageViewEvent function
        dispatchPageViewEvent(
          router,
          { detail: 'checkout-end', pageType: null, section: null },
          user,
          challenge,
        );
      }
    } catch (err) {
      console.error('Error completing checkout:', err);
      notification.error({
        message: formatMessage({ id: 'app.error.generic' }),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScrollTest = () => {
    const testResource = challenge.challengeResources.find(
      (item) => item.type === ResourceTypeEnum.QUIZ_TYPEFORM
    );
    if (testResource) {
      scrollDown(testResource.key);
    }
  };

  const handleClickButton = () => {
    if (config.hasOrderStep) {
      setShowModal(true);
    } else {
      performLotteryCheckout();
    }
  };

  const checkoutLotteryModal = () => {
    return (
      <Modal
        visible={showModal}
        className="checkout-modal"
        footer={null}
        mask={false}
        maskTransitionName={''}
        transitionName={''}
        onCancel={() => setShowModal(false)}
      >
        <BasicHeader />

        <div className="experience-checkout-lottery__modal__wrapper">
          <div className="checkout-modal__back-donation">
            <button onClick={() => setShowModal(false)} className="btn-return">
              <LeftOutlined />
              {formatMessage({ id: 'page.experience.checkout.back' })}
            </button>
          </div>

          <div className="experience-checkout-lottery__modal__title">
            {orderTitle
              ? orderTitle
              : formatMessage({
                  id: 'page.experience.checkout.summary-label',
                })}
          </div>

          <div className="experience-checkout-lottery__detail-wrapper">
            <CheckoutLotteryDetails
              challengeType={challenge?.challengeType}
              checkoutPoints={remainingChallengePoints}
              image={config.image}
              performCheckout={performLotteryCheckout}
              termsLabel={config.termsLabel}
              termsUrl={config.termsUrl}
              text={config.text}
              title={config.title}
              userPoints={userPoints}
              loading={loading}
            />
          </div>
        </div>
      </Modal>
    );
  };

  const checkoutLotterySuccessModal = () => {
    return (
      <Modal
        visible={showSuccessModal}
        className="checkout-modal"
        footer={null}
        mask={false}
        maskTransitionName={''}
        transitionName={''}
        onCancel={() => setShowModal(false)}
      >
        <BasicHeader />
        <CheckoutEndPage configCheckoutLottery={config} />
      </Modal>
    );
  };

  const checkoutLotteryButtonBalance = () => {
    const renderBalance =
      challenge?.challengePoints || challenge?.challengePoints === 0;
    if (!renderBalance) return <Fragment />;

    if (config.isBlocked) {
      return (
        <div className="experience-checkout-button__points experience-checkout-lottery-button">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              <Button
                className="btn-ghost--minimalist experience-checkout-lottery-button__link"
                onClick={handleScrollTest}
              >
                {customFormatMessage(
                  'page.experience.checkout.test-dependency'
                )}
              </Button>
            </p>
          </div>
        </div>
      );
    }

    if (!hasUserEnoughPoints) {
      return (
        <div className="experience-checkout-button__points">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              {customFormatMessage(
                'page.experience.checkout.user-balance-lacks-{points}',
                {
                  points: Number(pointsLabel - userPoints),
                }
              )}
            </p>
          </div>

          <div className="experience-checkout-button__points-link">
            <Link href={routePaths.PAGES.SCAN.SCAN_INDEX}>
              <Button className="btn-line">
                {formatMessage({
                  id: 'page.experience.checkout.user-balance-get-more-points',
                })}
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    if (textTopButton) {
      return (
        <div className="experience-checkout-button__points">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              {textTopButton}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="experience-checkout-button__points">
        <div className="experience-checkout-button__points-balance">
          <p className="regular-body experience-checkout-button__points-balance__text">
            {customFormatMessage(
              'page.experience.checkout.user-balance-has-points'
            )}
          </p>
        </div>
      </div>
    );
  };

  const buttonContent = (
    <>
      {checkoutLotteryButtonBalance()}
      <Button
        disabled={config.isBlocked || !hasUserEnoughPoints || user.blacklisted}
        className="btn-primary btn-primary--black experience-checkout-button experience-checkout-lottery-button__button"
        onClick={handleClickButton}
      >
        <span>{buttonName}</span>
      </Button>
    </>
  );

  const checkoutButton = (
    <>
      <div className="show_on_breakpoint-md experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow experience-checkout-lottery-button">
        {buttonContent}
      </div>
      <div className="not_show_on_breakpoint-md experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        <div className="container experience-checkout-button__container">
          <div>
            <img
              className="experience-checkout-button__container--image"
              src={config.image}
              alt="checkout-img"
            />
            <div className="experience-checkout-button__container--title">
              <span>{orderDescription ? orderDescription : config.title}</span>
            </div>
          </div>
          <div>{buttonContent}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!isCompleted && showCheckoutButton && checkoutButton}
      {checkoutLotteryModal()}
      {checkoutLotterySuccessModal()}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutLottery);
